import { useDisclosure } from '@chakra-ui/react'
import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { rankItem } from '@tanstack/match-sorter-utils'
import type { FilterFn } from '@tanstack/react-table'
import moment from 'moment'
import React, { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import AddCircleIcon from '../../../../../assets/icons/add-circle-stroke-rounded'
import AddWalletModal from '../../../../../components/AddWalletModal'
import InfoBox, {
  type InfoBoxColorscheme,
} from '../../../../../components/InfoBox'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import { useGetAllWalletsQuery } from '../../../../../redux/services/coreApi'
import { setSystemWalletsTableState } from '../../../../../redux/slices/tableFiltersSlice'
import { RootState } from '../../../../../redux/store'
import {
  WalletDto,
  WalletHostingType,
} from '../../../../../types/coreApi-types'
import EditWalletModal from './components/EditWalletModal'
import ImageAndValue from './components/ImageAndValue'

type SystemWalletsScreenProps = {}

// Helper function to get wallet type display info
const getWalletTypeDisplay = (hostingType: WalletHostingType) => {
  switch (hostingType) {
    case 'SelfHosted':
      return { text: 'Self Hosted', color: 'gray', icon: faCheck }
    case 'VaspHosted':
      return { text: 'VASP Hosted', color: 'green', icon: faCheck }
    case 'Unknown':
    default:
      return { text: 'Unknown', color: 'red', icon: faExclamationTriangle }
  }
}

// Wallet type filter options
type WalletTypeFilter = 'all' | 'inbound' | 'outbound'

// Custom global filter function for wallet search
const walletGlobalFilter: FilterFn<WalletDto> = (
  row,
  columnId,
  value,
  addMeta
) => {
  if ((row.original as any)['isLoading']) return true

  const searchValue = value.toLowerCase()
  const wallet = row.original

  // Create searchable text from all relevant fields
  const searchableText = [
    wallet.name || '',
    wallet.address || '',
    wallet.destinationTag || '',
    wallet.hostingType || '',
    wallet.hostedVaspId || '',
    wallet.network?.name || '',
    wallet.network?.isoCode || '',
  ]
    .join(' ')
    .toLowerCase()

  const itemRank = rankItem(searchableText, searchValue)

  addMeta({
    itemRank,
  })

  return itemRank.passed
}

const SystemWalletsScreen: React.FC<SystemWalletsScreenProps> = () => {
  const { data: wallets, isLoading } = useGetAllWalletsQuery()
  const [selectedWallet, setSelectedWallet] = useState<WalletDto | null>(null)
  const [walletTypeFilter, setWalletTypeFilter] =
    useState<WalletTypeFilter>('all')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const addWalletModal = useDisclosure()

  const systemWalletsTableState = useSelector(
    (state: RootState) => state.systemWalletsTable
  )

  // Filter wallets based on wallet type filter
  const filteredWallets = useMemo(() => {
    if (!wallets) return []

    return wallets.filter(wallet => {
      const walletName = wallet.name?.toLowerCase() || ''

      if (walletTypeFilter === 'all') return true
      if (walletTypeFilter === 'inbound') return walletName.includes('inbound')
      if (walletTypeFilter === 'outbound')
        return walletName.includes('outbound')

      return true
    })
  }, [wallets, walletTypeFilter])

  const handleEditWallet = (wallet: WalletDto) => {
    setSelectedWallet(wallet)
    onOpen()
  }

  const handleCloseEditModal = () => {
    onClose()
    setSelectedWallet(null)
  }

  return (
    <PageWrapper title="System Wallets Overview">
      {/* Wallet Type Filter Buttons */}
      <div className="flex gap-2 mb-4">
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            walletTypeFilter === 'all' ? 'button' : 'button-secondary'
          }`}
          onClick={() => setWalletTypeFilter('all')}
        >
          All Wallets
        </button>
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            walletTypeFilter === 'inbound' ? 'button' : 'button-secondary'
          }`}
          onClick={() => setWalletTypeFilter('inbound')}
        >
          Inbound Only
        </button>
        <button
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            walletTypeFilter === 'outbound' ? 'button' : 'button-secondary'
          }`}
          onClick={() => setWalletTypeFilter('outbound')}
        >
          Outbound Only
        </button>
      </div>

      <Table
        tableState={systemWalletsTableState}
        setTableState={setSystemWalletsTableState}
        navigate={() => null}
        isLoading={isLoading}
        onClickRow={(wallet: WalletDto) => handleEditWallet(wallet)}
        emptyDataRender={
          <div className="text-xl mb-3">No system wallets found</div>
        }
        customGlobalFilterFn={walletGlobalFilter}
        // @ts-expect-error
        defaultData={[
          ...(filteredWallets?.map(item => {
            return {
              ...item,
              active: item.active ? 'Active' : 'Inactive',
            }
          }) ?? []),
        ]}
        schema={[
          {
            header: 'Network',
            accessorKey: 'network',
            type: 'string',
            priority: 1,
            enableGlobalFilter: true,
            cellValueWrapper: (value, originalValue, { original }) => (
              <div className="w-min">
                <ImageAndValue
                  value={original?.network?.name || 'Unknown'}
                  iconUrl={original?.network?.iconUrl}
                />
              </div>
            ),
          },
          {
            header: 'Name/Address',
            accessorKey: 'address',
            type: 'string',
            priority: 2,
            enableGlobalFilter: true,
            cellValueWrapper: (value, originalValue, { original }) => (
              <div className="w-min">
                <ImageAndValue value={original?.name || value} tooltip />
              </div>
            ),
          },
          {
            header: 'Wallet Name',
            accessorKey: 'name',
            type: 'string',
            priority: 2.1,
            enableGlobalFilter: true,
            cellValueWrapper: (value, originalValue, { original }) => (
              <div className="w-min">
                {value ? (
                  <ImageAndValue value={value} tooltip />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
            ),
          },
          {
            header: 'Destination Tag',
            accessorKey: 'destinationTag',
            type: 'string',
            priority: 3,
            enableGlobalFilter: true,
            cellValueWrapper: value => (
              <div className="w-min">
                {value ? (
                  <ImageAndValue value={value} tooltip />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
            ),
          },
          {
            header: 'Status',
            accessorKey: 'active',
            type: 'status',
            priority: 4,
            cellValueWrapper: (value, originalValue, row) => (
              <div className="w-min flex flex-col">
                <InfoBox colorScheme={value === 'Active' ? 'green' : 'red'}>
                  {value === 'Active' ? 'Active' : 'Inactive'}
                </InfoBox>
              </div>
            ),
          },
          {
            header: 'Wallet Type',
            accessorKey: 'hostingType',
            type: 'string',
            priority: 5,
            enableGlobalFilter: true,
            cellValueWrapper: (value, originalValue, { original }) => {
              const typeDisplay = getWalletTypeDisplay(
                original?.hostingType || 'Unknown'
              )
              return (
                <div className="w-min flex items-center gap-2">
                  <InfoBox
                    colorScheme={typeDisplay.color as InfoBoxColorscheme}
                  >
                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon
                        icon={typeDisplay.icon}
                        className={`w-3 h-3 ${typeDisplay.color === 'red' ? 'text-red-500' : typeDisplay.color === 'green' ? 'text-green-500' : 'text-gray-500'}`}
                      />
                      <span>{typeDisplay.text}</span>
                    </div>
                  </InfoBox>
                </div>
              )
            },
          },
          {
            header: 'Hosted VASP ID',
            accessorKey: 'hostedVaspId',
            type: 'string',
            priority: 5.1,
            enableGlobalFilter: true,
            cellValueWrapper: value => (
              <div className="w-min">
                {value ? (
                  <ImageAndValue value={value} tooltip />
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
            ),
          },
          {
            header: 'Updated At',
            accessorKey: 'updatedAt',
            type: 'string',
            priority: 6,
            cellValueWrapper: value => (
              <div>
                {value ? (
                  moment.utc(value).local().format('MMM D, YYYY HH:mm')
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
            ),
          },
        ]}
      />

      <div className="flex justify-end mt-6">
        <button
          className="button flex items-center gap-2 bg-primary text-white hover:bg-primary-dark transition-colors"
          onClick={addWalletModal.onOpen}
        >
          <AddCircleIcon width={20} height={20} color="white" />
          <span>Add Wallet</span>
        </button>
      </div>

      <EditWalletModal
        isOpen={isOpen}
        onClose={handleCloseEditModal}
        wallet={selectedWallet}
      />
      <AddWalletModal
        isOpen={addWalletModal.isOpen}
        onClose={addWalletModal.onClose}
        systemWallet={true}
      />
    </PageWrapper>
  )
}

export default SystemWalletsScreen
